import React from 'react';

import type { GameState, Player } from '../../types/game';
import type { OnlineClientRole } from '../../types/transport';
import type { GameAction } from '../gameReducer';
import { useGameEffects } from './useGameEffects';
import { useOnlineEvents } from './useOnlineEvents';

type JoinGame = (
  gameId: string,
  options?: { groupToken?: string | null; skipGroupLookup?: boolean; assumeHost?: boolean },
) => Promise<boolean>;

type RefreshReason = 'manual' | 'score-sync' | string;

export type UseOnlineClientOrchestratorArgs = {
  state: GameState;
  dispatch: React.Dispatch<GameAction>;
  client: {
    playerId: string | null;
    setPlayerId(value: string | null): void;
    isHost: boolean;
    setIsHost(value: boolean): void;
  };
  joinGame: JoinGame;
  modeHint?: GameState['mode'];
  onPlayerReadyApplied?: (playerId: string) => void;
};

export type UseOnlineClientOrchestratorResult = {
  refreshSnapshot(reason?: RefreshReason): Promise<boolean>;
};

function deriveCurrentClientRole(
  players: Player[],
  currentClientPlayerId: string | null,
  isHost: boolean,
): OnlineClientRole | null {
  if (isHost) return 'host';
  const player = currentClientPlayerId
    ? players.find((candidate) => candidate.id === currentClientPlayerId)
    : null;
  if (!player) return null;
  return player.role === 'viewer' ? 'viewer' : 'player';
}

export function useOnlineClientOrchestrator({
  state,
  dispatch,
  client,
  joinGame,
  modeHint,
  onPlayerReadyApplied,
}: UseOnlineClientOrchestratorArgs): UseOnlineClientOrchestratorResult {
  const ignorePendingReadyChanges = React.useCallback(() => undefined, []);

  const {
    refreshSnapshot,
    onOnlineEventApplied,
    onOnlineStatusChange,
    onOnlineSnapshot,
    onOnlineServerError,
  } = useGameEffects({
    gameState: state,
    dispatch,
    isHost: client.isHost,
    setIsHost: client.setIsHost,
    currentClientPlayerId: client.playerId,
    setCurrentClientPlayerId: client.setPlayerId,
    lobbyPendingReadyChanges: new Set<string>(),
    setPendingReadyChanges: ignorePendingReadyChanges,
    joinGame,
    modeHint,
  });

  const currentClientPlayerName = React.useMemo(() => (
    client.playerId
      ? (state.players.find((player) => player.id === client.playerId)?.name || null)
      : null
  ), [state.players, client.playerId]);

  const currentClientRole = React.useMemo(
    () => deriveCurrentClientRole(state.players, client.playerId, client.isHost),
    [state.players, client.playerId, client.isHost],
  );

  useOnlineEvents({
    gameId: state.id,
    dispatch,
    currentClientPlayerId: client.playerId,
    currentClientPlayerName,
    currentClientRole,
    onPlayerReadyApplied,
    onOnlineEventApplied,
    onOnlineStatusChange,
    onOnlineSnapshot,
    onOnlineServerError,
  });

  return {
    refreshSnapshot,
  };
}
